import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { Container } from 'reactstrap';

import LoginBackground from '../pages/Authentication/LoginBackground';
import {
  PublicLanguageProvider,
  usePublicLanguage,
} from '../pages/PublicTenderAnnouncement/i18n/LanguageContext';
import { LANGUAGES } from '../pages/PublicTenderAnnouncement/i18n/translations';

/**
 * Layout untuk halaman publik (tanpa login).
 *
 * Latar memakai ulang `LoginBackground` dan kelas `.login-corporate` milik
 * halaman login persis apa adanya — gradasi, ombak berlapis, gelembung, dan
 * gerak mengikuti kursor. Komponen login TIDAK diubah sama sekali; halaman ini
 * hanya menumpang kelasnya, dengan satu penyesuaian di `_public-landing.scss`:
 * lapisan ombak dibuat `fixed` sebab halaman ini panjang dan bisa digulir,
 * sedangkan halaman login isinya selalu satu layar.
 *
 * Sengaja TIDAK memakai PermissionProvider, token, maupun useProfile untuk
 * gating — halaman ini harus tetap tampil sempurna bagi pengunjung anonim.
 *
 * Pemilihan bahasa memakai provider sendiri (lihat i18n/LanguageContext.js),
 * bukan i18next global, supaya tidak mengubah bahasa aplikasi ber-login.
 */
const PublicLayoutInner = ({ children, showBackHome = false }) => {
  const selectLayoutState = state => state.Layout;
  const selectLayoutProperties = createSelector(selectLayoutState, layout => ({
    layoutThemeType: layout.layoutThemeType,
    layoutModeType: layout.layoutModeType,
  }));

  const { layoutModeType, layoutThemeType } = useSelector(
    selectLayoutProperties
  );
  const { language, setLanguage, t } = usePublicLanguage();

  useEffect(() => {
    document.body.setAttribute('data-theme', layoutThemeType);
    document.body.setAttribute(
      'data-bs-theme',
      layoutModeType === 'dark' ? 'dark' : 'light'
    );
    return () => {
      document.body.removeAttribute('data-bs-theme');
      document.body.removeAttribute('data-theme');
    };
  }, [layoutModeType, layoutThemeType]);

  // Bantu pembaca layar & mesin telusur mengenali bahasa halaman.
  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  const currentYear = new Date().getFullYear();

  return (
    <div className='login-corporate public-landing-shell d-flex flex-column min-vh-100'>
      <LoginBackground />

      <Container fluid className='public-landing__container position-relative'>
        <div className='public-landing__topbar'>
          {/* Hanya halaman daftar yang menampilkannya. Halaman detail sudah punya
              tautan kembali sendiri, dan landing memang tujuan tombol ini. */}
          {showBackHome && (
            <Link to='/' className='public-landing__home-btn'>
              <i className='ri-home-4-line' aria-hidden='true' />
              <span>{t('layout.backHome')}</span>
            </Link>
          )}

          <div
            className='public-landing__lang'
            role='group'
            aria-label={t('layout.language')}
          >
            {LANGUAGES.map(item => (
              <button
                key={item.code}
                type='button'
                lang={item.code}
                title={item.label}
                aria-pressed={language === item.code}
                className={`public-landing__lang-btn ${
                  language === item.code ? 'is-active' : ''
                }`}
                onClick={() => setLanguage(item.code)}
              >
                {item.short}
              </button>
            ))}
          </div>
        </div>

        <main className='public-landing__main flex-grow-1'>{children}</main>

        <footer className='public-landing__footer'>
          <span>{t('layout.copyright', { year: currentYear })}</span>
          <Link to='/privacy-policy' className='public-landing__footer-link'>
            {t('layout.privacy')}
          </Link>
        </footer>
      </Container>
    </div>
  );
};

const PublicLayout = ({ children, showBackHome = false }) => (
  <PublicLanguageProvider>
    <PublicLayoutInner showBackHome={showBackHome}>
      {children}
    </PublicLayoutInner>
  </PublicLanguageProvider>
);

export default PublicLayout;
